import { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useAuth } from "@src/common/hooks/useAuth";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";
import { RouteStatus } from "@src/routes/types";
import { useShipmentFiltersContext } from "@src/shipments/context/ShipmentFiltersContext";
import { RoutesAttentionRoute, RoutesAttentionSummary } from "./types";

// Deliberately lean: no stopsInSequence. The Orders page only needs the
// route-level status the backend already rolls up (LiveRouteStatusCalculator),
// not every stop/task/order of the day.
export const LIVE_ROUTES_ATTENTION = gql`
  query LiveRoutesAttention(
    $courierId: ID!
    $minDate: Date
    $maxDate: Date
    $limit: Int
    $after: Int
  ) {
    liveRoutesPaginated(
      courierId: $courierId
      minDate: $minDate
      maxDate: $maxDate
      limit: $limit
      after: $after
    ) {
      edges {
        node {
          id
          driverId
          driver {
            id
            firstName
            lastName
          }
          routeTemplate {
            id
            name
            displayId
          }
          statusList {
            title
            type
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

// Hardcoded for the Vital relaunch (agreed with Temuge, 2026-09-03). Other
// couriers don't want this yet; replace with courier-level config when more
// couriers ask for it.
export const ROUTES_ATTENTION_COURIER_IDS = [
  "COURcc5dbe3b003142e4", // Awesome Courier (Local)
  "COURb56ce44592ab4162", // Vital Delivery Solutions (Prod)
  "COURJAKOBDEMO", // Jakob Demo Account (Prod) — requested in #releases 2026-09-14
];

export const PAGE_SIZE = 200;
// Hard stop for the follow-up page fetches so a runaway range can't loop.
export const MAX_ROUTES = 1000;
const POLL_INTERVAL_MS = 60 * 1000;

interface RawLiveRoute {
  id: string;
  driverId: string | null;
  driver: { id: string; firstName: string; lastName: string } | null;
  routeTemplate: { id: string; name: string; displayId: number } | null;
  statusList: RouteStatus[] | null;
}

interface LiveRoutesAttentionResponse {
  liveRoutesPaginated: {
    edges: Array<{ node: RawLiveRoute }>;
    pageInfo: { hasNextPage: boolean };
  };
}

interface UseRoutesAttentionResult {
  routes: RoutesAttentionRoute[];
  summary: RoutesAttentionSummary;
  routesPageHref: string;
  isLoading: boolean;
  // Last refresh failed; `routes` is the previous (possibly stale) result.
  isStale: boolean;
}

// Mirrors the backend: DANGER ("Late") outranks WARNING ("At Risk");
// an unassigned route has no badge but still needs a dispatcher.
const SEVERITY_RANK: Record<RouteStatus["type"], number> = {
  DANGER: 0,
  WARNING: 1,
  INFO: 3,
  SUCCESS: 3,
};
const UNASSIGNED_RANK = 2;
const IGNORE_RANK = 3;

function severity(route: RoutesAttentionRoute): number {
  const badgeRank = Math.min(
    IGNORE_RANK,
    ...route.statusList.map((status) => SEVERITY_RANK[status.type])
  );
  return route.driver ? badgeRank : Math.min(badgeRank, UNASSIGNED_RANK);
}

function needsAttention(route: RoutesAttentionRoute): boolean {
  return severity(route) < IGNORE_RANK;
}

// Each route counts once, under its most severe reason, so the counts add
// up to routeCount: "2 Late · 1 At Risk · 1 Unassigned".
export function summarize(routes: RoutesAttentionRoute[]): RoutesAttentionSummary {
  const ranks = routes.map(severity);
  return {
    routeCount: routes.length,
    lateCount: ranks.filter((r) => r === SEVERITY_RANK.DANGER).length,
    atRiskCount: ranks.filter((r) => r === SEVERITY_RANK.WARNING).length,
    unassignedCount: ranks.filter((r) => r === UNASSIGNED_RANK).length,
    headlineRoute: routes[0],
  };
}

function toRoute(raw: RawLiveRoute): RoutesAttentionRoute {
  return {
    id: raw.id,
    routeTemplate: raw.routeTemplate ?? {
      id: raw.id,
      name: "Ad-hoc route",
      displayId: 0,
    },
    driver: raw.driver
      ? {
          id: raw.driver.id,
          fullName: `${raw.driver.firstName} ${raw.driver.lastName}`.trim(),
        }
      : null,
    statusList: raw.statusList ?? [],
  };
}

export function selectRoutesNeedingAttention(
  data: LiveRoutesAttentionResponse | undefined
): RoutesAttentionRoute[] {
  const edges = data?.liveRoutesPaginated?.edges ?? [];
  return edges
    .filter((edge) => Boolean(edge?.node))
    .map((edge) => toRoute(edge.node))
    .filter(needsAttention)
    .sort((a, b) => severity(a) - severity(b));
}

export function useRoutesAttention(): UseRoutesAttentionResult {
  const { courierId } = useAuth();
  const isEnabled =
    courierId !== null && ROUTES_ATTENTION_COURIER_IDS.includes(courierId);
  // Follow the Orders page date paginator so the banner and the table
  // describe the same range. An undefined bound stays unbounded (null), the
  // same way makeGetShipmentsVariable treats it for the Orders query.
  const { minDate: filterMinDate, maxDate: filterMaxDate } =
    useShipmentFiltersContext();
  const minDate = filterMinDate ? datePickerDateToISODate(filterMinDate) : null;
  const maxDate = filterMaxDate ? datePickerDateToISODate(filterMaxDate) : null;

  const { data, loading, error, fetchMore } =
    useQuery<LiveRoutesAttentionResponse>(LIVE_ROUTES_ATTENTION, {
      variables: {
        courierId: courierId!,
        minDate,
        maxDate,
        limit: PAGE_SIZE,
        after: 0,
      },
      skip: !isEnabled,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      pollInterval: POLL_INTERVAL_MS,
    });

  // Walk the remaining pages so a busy day (Vital peaked at 126 routes/day)
  // is never under-counted by the first page alone.
  const loadedCount = data?.liveRoutesPaginated?.edges?.length ?? 0;
  const hasNextPage = data?.liveRoutesPaginated?.pageInfo?.hasNextPage ?? false;
  // A failed follow-up page leaves `data` holding a partial result that the
  // parent query's `error` knows nothing about, so track it separately.
  const [hasPageError, setHasPageError] = useState(false);
  useEffect(() => setHasPageError(false), [minDate, maxDate]);
  useEffect(() => {
    if (!hasNextPage || loadedCount >= MAX_ROUTES) return;
    fetchMore({
      variables: { after: loadedCount },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;
        return {
          liveRoutesPaginated: {
            ...fetchMoreResult.liveRoutesPaginated,
            edges: [
              ...previous.liveRoutesPaginated.edges,
              ...fetchMoreResult.liveRoutesPaginated.edges,
            ],
          },
        };
      },
    }).catch(() => setHasPageError(true));
  }, [hasNextPage, loadedCount, fetchMore]);

  const routes = useMemo(
    () => (isEnabled ? selectRoutesNeedingAttention(data) : []),
    [data, isEnabled]
  );

  return {
    routes,
    summary: summarize(routes),
    // Routes table page (CRI-122, PR #507). Its deep link is a single day, so
    // open the range start; with no start the range is unbounded and any
    // single day would exclude routes the banner counted, so let the page
    // pick its default instead.
    routesPageHref: minDate
      ? `/routes/table?date=${minDate}`
      : "/routes/table",
    isLoading: loading && !data,
    isStale: (Boolean(error) || hasPageError) && Boolean(data),
  };
}
